import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import {
  AdModel,
  NotificationTypeEnum,
  State,
  HeaderDialogsEnum,
  CollectionModel,
  AdCollectionModel,
} from '@/types';
import { collectionsProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { Loader } from '@/components';
import { AdsCollections } from './AdsCollections';
import { AdsListSelect } from './AdsListSelect';
import { EmptyList } from '../EmptyList';
import IconArrow from './iconArrow.svg';
import styles from './AdsMyProperty.module.scss';
import { useDialogs } from '@/components/Dashboard/DialogsContext';

export const Collection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setOpenDialog } = useDialogs();
  const { user } = useSelector(({ auth }: State) => auth);
  const [isLoadingGetAds, setIsLoadingGetAds] = useState<boolean>(false);
  const [ads, setAds] = useState<AdCollectionModel[]>([]);
  const [collectionName, setCollectionName] = useState<string>();
  const [collectionId, setCollectionId] = useState<string>();
  const [selectedAds, setSelectedAds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [countSelected, setCountSelected] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

  const getCollectionById = async () => {
    setIsLoadingGetAds(true);
    const { ok, data, status, message } = await collectionsProvider.getCollectionById(id!);
    if (ok && data) {
      const { items, name, collectionId } = data as CollectionModel;
      setAds(items);
      setCollectionName(name);
      setCollectionId(collectionId.toString());
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
    setIsLoadingGetAds(false);
  };

  const deleteListing = async () => {
    if (!collectionId || !selectedDeleteId) return;
    setIsLoading(true);
    const { ok, status, message } = await collectionsProvider.deleteListing(
      collectionId,
      selectedDeleteId
    );
    if (ok) {
      getCollectionById();
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
    setIsLoading(false);
    setSelectedDeleteId(null);
  };

  const deleteListings = async () => {
    if (!collectionId || selectedAds.length === 0) return;
    setIsLoading(true);
    try {
      const results = await Promise.all(
        selectedAds.map((adId) => collectionsProvider.deleteListing(collectionId, adId))
      );
      const failed = results.find((res) => !res.ok);
      if (failed) {
        dispatch(appSetNotification(NotificationTypeEnum.Error, failed.message, failed.status));
      }
      await getCollectionById();
      setSelectedAds([]);
    } catch (err) {
      console.error('Error while deleting listings:', err);
      dispatch(appSetNotification(NotificationTypeEnum.Error, 'Something went wrong', 500));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getCollectionById();
  }, []);

  useEffect(() => {
    if (selectedAds.length > 0) {
      setCountSelected(selectedAds.length);
    } else {
      setCountSelected(null);
    }
    if (ads && ads?.length > 0 && selectedAds.length === ads?.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedAds]);

  const handleClickOpenDialog = (dialogType: HeaderDialogsEnum) => {
    setOpenDialog((prevState) => ({
      ...prevState,
      [dialogType]: !prevState[dialogType],
    }));
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedAds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((itemId) => itemId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedAds([]);
    } else {
      setSelectedAds((ads as AdModel[]).map((ad) => ad.id));
    }
    setSelectAll(!selectAll);
  };

  if (isLoadingGetAds || isLoading) {
    return <Loader />;
  }

  return (
    <Box className={styles.adsContainer}>
      <Box className={styles.title}>
        <span className={styles.text}>{collectionName}</span>
      </Box>
      <Box className={styles.templateMyAdsContainer}>
        <Box onClick={() => navigate('/favorites')} className={styles.btnBack}>
          <img src={IconArrow} alt='' style={{ cursor: 'pointer' }} />
          <Typography sx={{ cursor: 'pointer', fontWeight: '800' }}>Back to Collections</Typography>
        </Box>
        {!ads?.length ? (
          <EmptyList />
        ) : (
          <>
            <AdsListSelect
              selectedAds={selectedAds}
              selectAll={selectAll}
              handleSelectAllChange={handleSelectAllChange}
              countSelected={countSelected}
              deleteListings={deleteListings}
            />
            <Box className={styles.myAdsList}>
              {(ads as AdCollectionModel[]).map((item) => (
                <AdsCollections
                  key={item.id}
                  item={item}
                  user={user}
                  selectedAds={selectedAds}
                  handleCheckboxChange={handleCheckboxChange}
                  handleClickOpenDialog={handleClickOpenDialog}
                  onClose={() => setSelectedDeleteId(null)}
                  onOpenDelete={(id) => setSelectedDeleteId(id)}
                  deleteListing={deleteListing}
                  open={selectedDeleteId === item.id}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
